import { ComponentType, lazy } from "react";
import {
    getKeyWithReleaseVersion,
    getSessionStorageItem,
    parseJSONSafely,
    setSessionStorageItem,
} from "utils/service";

/**
 * Recovery for the "oops, something went wrong" screen that appears when a
 * browser tab which loaded an OLD index.html tries to lazy-load a route chunk
 * after the workspace was upgraded while the tab was open.
 *
 * Vite emits content-hashed chunk filenames (e.g. `index-<hash>.js`). Once a
 * new build is deployed the old hashes no longer exist on the server, so the
 * SPA fallback serves `index.html` (`text/html`) instead of the JS module,
 * strict-MIME rejects it, and the dynamic `import()` throws. A single
 * full-page reload fetches the fresh `index.html` (with the new chunk names)
 * and recovers transparently — which is exactly what the user does by hand.
 */

const RELOAD_FLAG_KEY = "stale-chunk-reload";

/**
 * How long a recorded reload attempt suppresses another one. Long enough to
 * break a genuine reload loop (a truly-broken build reloads and fails again
 * within a second or two), short enough that a legitimate later deploy still
 * earns a fresh reload instead of the generic error screen.
 */
const RELOAD_SUPPRESS_WINDOW_MS = 15_000;

const CHUNK_ERROR_PATTERNS: RegExp[] = [
    /failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /importing a module script failed/i, // Safari
    /failed to load module script/i, // Chrome strict-MIME rejection
    /expected a javascript.{0,12}module script/i,
    /module script.{0,40}text\/html/i, // index.html served in place of the chunk
    /chunkloaderror/i,
    /loading chunk [\w-]+ failed/i, // webpack-style, defensive
];

/** Best-effort extraction of a comparable message string from any thrown value. */
const errorText = (error: unknown): string => {
    if (!error) {
        return "";
    }
    if (typeof error === "string") {
        return error;
    }
    if (error instanceof Error) {
        return `${error.name}: ${error.message}`;
    }
    const maybe = error as { message?: unknown };
    if (typeof maybe.message === "string") {
        return maybe.message;
    }
    try {
        return String(error);
    } catch {
        return "";
    }
};

/**
 * True when the error is the stale-chunk / failed-dynamic-import signature seen
 * after a deploy (as opposed to an ordinary application error).
 */
export const isChunkLoadError = (error: unknown): boolean => {
    const text = errorText(error);
    if (!text) {
        return false;
    }
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

/**
 * Trigger exactly ONE full-page reload to recover from a stale chunk after a
 * deploy. A release-scoped sessionStorage flag records the attempt so a
 * genuinely-missing chunk cannot cause an endless reload loop: a second failure
 * within RELOAD_SUPPRESS_WINDOW_MS is NOT reloaded, letting the caller surface
 * the real error instead.
 *
 * @returns true when a reload was triggered (caller should keep the loading
 *   state and stop rendering the error); false when suppressed (caller should
 *   surface the error boundary).
 */
export const reloadForStaleChunkOnce = (): boolean => {
    const now = Date.now();
    const last = parseJSONSafely(getSessionStorageItem(RELOAD_FLAG_KEY), 0);
    if (typeof last === "number" && last > 0 && now - last < RELOAD_SUPPRESS_WINDOW_MS) {
        // We already reloaded very recently and STILL hit the error — this is
        // not a transient stale-deploy. Give up so we don't loop.
        return false;
    }
    setSessionStorageItem(RELOAD_FLAG_KEY, now);
    console.warn("Stale route chunk after deploy — reloading to fetch the new build.");
    window.location.reload();
    return true;
};

/**
 * Clear the reload guard once the app has successfully loaded a route chunk:
 * the app is on the current build, so a future deploy is free to reload again.
 */
export const clearStaleChunkReloadFlag = (): void => {
    try {
        sessionStorage.removeItem(getKeyWithReleaseVersion(RELOAD_FLAG_KEY));
    } catch {
        // sessionStorage unavailable (private mode/quota) — nothing to clear.
    }
};

/**
 * Drop-in replacement for `React.lazy` that auto-recovers from stale-chunk
 * dynamic-import failures after a deploy: on such an error it triggers exactly
 * one guarded full-page reload, so the user transparently gets the new build
 * instead of the "oops, something went wrong" screen. Ordinary (non-chunk)
 * errors, and a repeat chunk error after we already reloaded, are re-thrown so
 * the route error boundary handles them normally.
 */
export const lazyWithReload = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
) =>
    lazy(() =>
        factory()
            .then((module) => {
                // A route chunk loaded successfully → reset the guard so a
                // later deploy can reload again.
                clearStaleChunkReloadFlag();
                return module;
            })
            .catch((error: unknown) => {
                if (isChunkLoadError(error) && reloadForStaleChunkOnce()) {
                    // A reload is underway; return a never-resolving promise so
                    // React keeps showing the Suspense fallback until the page
                    // navigates away, instead of flashing the error boundary.
                    return new Promise<{ default: T }>(() => {});
                }
                throw error;
            })
    );
